<template>
  <!-- Edit-user → SIP extension of a call-center agent: generate it on the PBX,
       see whether it exists / is online, enable the line. Desktop (WebRTC) only.
       The SIP secret never reaches the browser (server-managed). -->
  <div class="tel-ui w-full mt-6 p-4 border border-slate-200 rounded-lg bg-slate-50 space-y-3">
    <div class="flex items-center justify-between gap-2">
      <div class="text-sm font-medium text-slate-800 flex items-center gap-2">
        <Icon icon="mdi:phone-classic" class="text-green-600" />
        {{ $t('tel_agent_section') }}
      </div>

      <!-- Create: only before an extension exists -->
      <button
        v-if="!tel.extension"
        type="button"
        :disabled="!user._id || creating"
        @click="createAccount(false)"
        class="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded-lg disabled:opacity-50"
        :title="!user._id ? $t('tel_save_user_first') : ''"
      >
        <Icon icon="mdi:auto-fix" class="text-sm" />
        {{ creating ? '…' : $t('tel_create_account') }}
      </button>
      <!-- Recreate: only when the extension is NOT active on the PBX (avoids an
           accidental secret reset) -->
      <button
        v-else-if="!status || status.existsInPbx !== true"
        type="button"
        :disabled="creating || checking"
        @click="createAccount(true)"
        class="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-xs px-3 py-1.5 rounded-lg disabled:opacity-50"
        :title="$t('tel_recreate_hint')"
      >
        <Icon icon="mdi:refresh" class="text-sm" />
        {{ creating ? '…' : $t('tel_recreate') }}
      </button>
    </div>

    <!-- No extension yet -->
    <template v-if="!tel.extension">
      <p class="text-xs text-slate-400">{{ $t('tel_create_hint') }}</p>
      <div v-if="user._id" class="flex gap-2 rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-700">
        <Icon icon="mdi:monitor" class="text-base flex-shrink-0 mt-0.5" />
        <span>{{ $t('tel_device_guide_desktop') }}</span>
      </div>
    </template>

    <!-- Extension exists → status + details -->
    <template v-else>
      <div v-if="checking" class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-100 text-slate-500">
        <Icon icon="eos-icons:three-dots-loading" /> {{ $t('tel_pbx_checking') }}
      </div>
      <div v-else-if="status" class="inline-flex flex-wrap items-center gap-2">
        <div v-if="status.existsInPbx === true" class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-100 text-green-700">
          <Icon icon="mdi:check-circle" /> {{ $t('tel_pbx_active') }}
        </div>
        <div v-else-if="status.existsInPbx === false" class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-100 text-red-700">
          <Icon icon="mdi:alert-circle" /> {{ $t('tel_pbx_missing') }}
        </div>
        <div v-else class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-100 text-slate-500">
          <Icon icon="mdi:help-circle" /> {{ $t('tel_pbx_unknown') }}
        </div>
        <div v-if="status.existsInPbx === true && status.online === true" class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-100 text-green-700">
          <Icon icon="mdi:lan-connect" /> {{ $t('tel_pbx_online') }}
        </div>
        <div v-else-if="status.existsInPbx === true && status.online === false" class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-amber-100 text-amber-700" :title="$t('tel_pbx_offline_hint')">
          <Icon icon="mdi:lan-disconnect" /> {{ $t('tel_pbx_offline') }}
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label class="block text-xs text-slate-500 mb-1">{{ $t('tel_extension') }}</label>
          <input :value="tel.extension" disabled class="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-100 cursor-not-allowed" />
        </div>
        <div>
          <label class="block text-xs text-slate-500 mb-1">{{ $t('tel_gateway_optional') }}</label>
          <input :value="tel.gateway" disabled class="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-100 cursor-not-allowed" />
        </div>
      </div>

      <div class="flex gap-2 rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-700">
        <Icon icon="mdi:monitor" class="text-base flex-shrink-0 mt-0.5" />
        <span>
          {{ $t('tel_device_desktop') }} — {{ $t('tel_device_guide_desktop') }}
          <span v-if="tel.extension" class="font-semibold">({{ $t('tel_device_ext') }} {{ tel.extension }})</span>
        </span>
      </div>

      <label class="flex items-center gap-2 text-sm">
        <input type="checkbox" v-model="tel.active" class="h-4 w-4 rounded border-slate-400" />
        {{ $t('tel_enable_agent') }}
      </label>
      <p class="text-xs text-slate-400">{{ $t('tel_sip_secret_note') }}</p>
    </template>
  </div>
</template>

<script>
import HTTP from "../../common/axios";

export default {
  name: "UserTelephony",
  props: {
    // The user being edited (the form's formData) — `telephony` is saved with it.
    user: { type: Object, required: true },
  },
  data() {
    return { creating: false, checking: false, status: null };
  },
  computed: {
    tel() {
      return this.user.telephony || {};
    },
  },
  created() {
    // Make sure the sub-object exists (and is reactive) so the inputs bind.
    if (!this.user.telephony) {
      this.$set(this.user, "telephony", { extension: "", warehouse: null, gateway: "", active: false, device: "desktop" });
    }
    // Desktop (WebRTC) is the only device — normalise old "mobile" accounts on the next save.
    if (this.user.telephony.device !== "desktop") this.$set(this.user.telephony, "device", "desktop");
  },
  watch: {
    "user._id": {
      immediate: true,
      handler() { this.checkExtension(); },
    },
  },
  methods: {
    // Does the stored extension still exist on the PBX / is the softphone online?
    // Silent on failure — the badge just stays "unknown".
    async checkExtension() {
      if (!this.user._id || !this.tel.extension) { this.status = null; return; }
      this.checking = true;
      try {
        const { data } = await HTTP.post("telephony/agentExtensionStatus", { userId: this.user._id });
        const c = data && data.content;
        this.status = c && c.ok && c.hasExtension
          ? { existsInPbx: c.existsInPbx, pbxConfigured: c.pbxConfigured, online: c.online }
          : null;
      } catch (e) {
        this.status = null;
      } finally {
        this.checking = false;
      }
    },

    async createAccount(recreate) {
      if (!this.user._id) { this.$toast.warning(this.$t("tel_save_user_first")); return; }
      if (recreate && !window.confirm(this.$t("tel_recreate_confirm"))) return;
      this.creating = true;
      try {
        const res = await this.$server.customPost("telephony/createAgentAccount", {
          userId: this.user._id,
          recreate: !!recreate,
          device: "desktop",
        });
        const c = res && res.content;
        if (c && c.ok) {
          this.$set(this.user, "telephony", { ...this.tel, extension: c.extension, active: true });
          const key = c.recreated ? "tel_account_recreated" : c.existing ? "tel_account_exists" : "tel_account_created";
          this.$toast[c.existing && !c.recreated ? "warning" : "success"](this.$t(key) + " " + c.extension);
          this.checkExtension();
        }
      } catch (e) {
        /* the API's message was already shown (alert) by the shared axios layer */
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>
